// src/pages/Admin/AdminDashboard.tsx
import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import api from "../../services/api";
import styles from "../../styles/Admin.module.css";

// TypeScript interfaces
interface Order {
  id: string;
  clientName: string;
  status: string;
  price: number;
  createdAt: string; // ISO date
}

interface StatusChartData {
  name: string;
  value: number;
}

interface RevenueChartData {
  month: string;
  revenue: number;
}

// Pie chart colors
const statusColors: Record<string, string> = {
  Pending: "#FFBB28",
  Completed: "#00C49F",
  Cancelled: "#FF8042",
};

const AdminDashboard: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  const [statusChart, setStatusChart] = useState<StatusChartData[]>([]);
  const [revenueChart, setRevenueChart] = useState<RevenueChartData[]>([]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await api.get("/orders");
        setOrders(res.data);

        // Prepare status chart data
        const statusCount: Record<string, number> = {};
        res.data.forEach((order: Order) => {
          statusCount[order.status] = (statusCount[order.status] || 0) + 1;
        });
        const statusData: StatusChartData[] = Object.entries(statusCount).map(([name, value]) => ({ name, value }));
        setStatusChart(statusData);

        // Prepare monthly revenue chart
        const monthlyRevenue: Record<string, number> = {};
        res.data.forEach((order: Order) => {
          const date = new Date(order.createdAt);
          const month = date.toLocaleString("default", { month: "short", year: "numeric" });
          monthlyRevenue[month] = (monthlyRevenue[month] || 0) + order.price;
        });
        const revenueData: RevenueChartData[] = Object.entries(monthlyRevenue)
          .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
          .map(([month, revenue]) => ({ month, revenue }));
        setRevenueChart(revenueData);
      } catch (err) {
        console.error("Error fetching orders:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  if (loading) return <p className={styles.adminHeader}>Loading...</p>;

  return (
    <div className={styles.adminContainer}>
      <h2 className={styles.adminHeader}>📍 Admin Dashboard</h2>

      <div className={styles.grid}>
        {/* Order Status Pie Chart */}
        <div className={`${styles.card} ${styles.p6} ${styles.rounded}`}>
          <h3 className={styles.adminHeader}>Order Status</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusChart as any} // cast to satisfy recharts
                dataKey="value"
                nameKey="name"
                label
              >
                {statusChart.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={statusColors[entry.name] || "#8884d8"} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly Revenue Line Chart */}
        <div className={`${styles.card} ${styles.p6} ${styles.rounded}`}>
          <h3 className={styles.adminHeader}>Monthly Revenue (UGX)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueChart}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="revenue" stroke="#4caf50" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Orders Table */}
      <div className={`${styles.card} ${styles.p6} ${styles.rounded}`}>
        <h3 className={styles.adminHeader}>All Orders</h3>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Customer</th>
              <th>Status</th>
              <th>Total (UGX)</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order.id}>
                <td>{order.clientName}</td>
                <td>
                  <span
                    style={{
                      backgroundColor: statusColors[order.status] || "#ccc",
                      color: "#fff",
                      padding: "4px 8px",
                      borderRadius: "4px",
                    }}
                  >
                    {order.status}
                  </span>
                </td>
                <td>{order.price.toLocaleString("en-US")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminDashboard;
